import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ZONA_HORARIA, formatFxRate, type Role } from '@abastece/shared';
import { db, getMeta, type MetaShape } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { emitir } from '../lib/emitir.js';
import { leerAlmacenamiento, pedirQueConserve, type Almacenamiento } from '../lib/almacenamiento.js';
import { elegirPapel, imprimirTexto, papelActual, textoPrueba, type Papel } from '../lib/imprimir.js';
import { salir, usePuede, useSesion } from '../lib/sesion.js';
import { EstadoSync } from '../ui/EstadoSync.js';
import { Idioma } from '../ui/Idioma.js';
import { PedirDato } from '../ui/Preguntar.js';
import { DatosDePrueba } from '../ui/DatosDePrueba.js';
import { CambiarMiPin } from './Personal.js';
import { localeActual, useT } from '../i18n/index.js';
import { etiquetaRol } from '../i18n/dominio.js';
import { Ico } from '../ui/Iconos.js';
import { BotonInicio, BotonVolver } from '../ui/Pagina.js';

export function Ajustes() {
  const t = useT();
  const sesion = useSesion();
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], null);
  const [meta, setMetaState] = useState<Partial<MetaShape>>({});
  const [almacen, setAlmacen] = useState<Almacenamiento | null>(null);
  const [renombrando, setRenombrando] = useState(false);
  const puedeConfigurar = usePuede('configurar');
  const puedeVerBitacora = usePuede('ver_bitacora');
  const puedeGestionarUsuarios = usePuede('gestionar_usuarios');
  const [cambiandoPin, setCambiandoPin] = useState(false);

  useEffect(() => {
    void (async () => {
      setMetaState({
        dispositivoCodigo: await getMeta('dispositivoCodigo'),
        ultimoServerSeq: await getMeta('ultimoServerSeq'),
        proximoDeviceSeq: await getMeta('proximoDeviceSeq'),
        ultimaSyncTs: await getMeta('ultimaSyncTs'),
        clockOffsetMs: await getMeta('clockOffsetMs'),
      });
      setAlmacen(await leerAlmacenamiento());
    })();
  }, []);

  async function pedirOtraVez() {
    await pedirQueConserve();
    setAlmacen(await leerAlmacenamiento());
  }

  const rechazados = useLiveQuery(
    () => db.eventos.where('estado').equals('rechazado').filter((e) => !e.rechazadoAqui).toArray(),
    [],
    [],
  );

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-5 pb-16">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BotonInicio />
          <h1 className="text-xl font-bold">{t('ajustes.titulo')}</h1>
        </div>
        <BotonVolver a="/" className="btn-secundario gap-1.5 pr-4 pl-3 text-sm" />
      </header>

      <div data-guia="ajustes-sync">
        <EstadoSync />
      </div>

      <section className="tarjeta" data-guia="ajustes-idioma">
        <Idioma />
      </section>

      <Impresora negocio={config?.negocioNombre ?? ''} />

      <section className="tarjeta flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-slate-400">{t('ajustes.esteDispositivo')}</h2>
        <div data-guia="ajustes-dispositivo">
          <Dato etiqueta={t('ajustes.codigo')} valor={meta.dispositivoCodigo ?? '—'} />
        </div>
        <Dato etiqueta={t('ajustes.ultimoRecibido')} valor={String(meta.ultimoServerSeq ?? 0)} />
        <Dato etiqueta={t('ajustes.proximoLocal')} valor={String(meta.proximoDeviceSeq ?? 1)} />
        <Dato
          etiqueta={t('ajustes.ultimaSync')}
          valor={
            meta.ultimaSyncTs
              ? new Date(meta.ultimaSyncTs).toLocaleString(localeActual(), {
                  timeZone: ZONA_HORARIA,
                })
              : t('ajustes.nunca')
          }
        />
        <Dato
          etiqueta={t('ajustes.desfaseReloj')}
          valor={`${Math.round((meta.clockOffsetMs ?? 0) / 1000)}s`}
        />
        <Dato
          etiqueta={t('ajustes.datos')}
          valor={almacen?.usado != null ? `${(almacen.usado / 1_048_576).toFixed(1)} MB` : '—'}
        />
        <Dato
          etiqueta={t('ajustes.conservados')}
          valor={almacen?.conservado == null ? '—' : t(almacen.conservado ? 'comun.si' : 'comun.no')}
        />
        {almacen?.conservado === false && (
          <div className="flex flex-col items-start gap-1 rounded-lg bg-amber-500/10 px-3 py-2">
            <p className="text-xs text-amber-200">{t('ajustes.ayudaConservar')}</p>
            <button
              type="button"
              className="btn-texto gap-1 text-xs text-amber-200"
              onClick={() => void pedirOtraVez()}
            >
              <Ico icono="guardar" className="size-3.5" />
              {t('ajustes.pedirConservar')}
            </button>
          </div>
        )}
        <p className="mt-1 text-xs text-slate-500">{t('ajustes.explicacionCodigo')}</p>
      </section>

      <section className="tarjeta flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-slate-400">{t('ajustes.configuracion')}</h2>
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-sm text-slate-400">{t('ajustes.negocio')}</span>
          <span className="flex items-baseline gap-3">
            <span className="tabular text-sm font-medium">{config?.negocioNombre ?? '—'}</span>
            {puedeConfigurar && (
              <button
                type="button"
                className="btn-texto gap-1 text-xs text-slate-500"
                onClick={() => setRenombrando(true)}
              >
                <Ico icono="editar" className="size-3.5" />
                {t('ajustes.cambiarNombre')}
              </button>
            )}
          </span>
        </div>
        <Dato
          etiqueta={t('ajustes.tasaTienda')}
          valor={config ? `C$ ${formatFxRate(config.fxTasaTienda)} / US$` : '—'}
        />
        <Dato
          etiqueta={t('ajustes.tasaOficial')}
          valor={config ? `C$ ${formatFxRate(config.fxTasaOficial)} / US$` : '—'}
        />
        <Dato
          etiqueta={t('ajustes.margenMinimo')}
          valor={config ? `${config.precioMargenMinimoBp / 100}%` : '—'}
        />
        <Dato
          etiqueta={t('ajustes.iva')}
          valor={t(config?.ivaHabilitado ? 'ajustes.ivaActivo' : 'ajustes.ivaInactivo')}
        />
        {/* The whole shop's settings, on a page of their own: these are read here, changed there. */}
        {puedeConfigurar && (
          <Link to="/ajustes/negocio" className="btn-secundario mt-2 h-12 min-h-0 gap-2 text-sm">
            <Ico icono="editar" className="size-4" />
            {t('ajustes.cambiarConfiguracion')}
          </Link>
        )}
      </section>

      <section className="tarjeta flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-slate-400">{t('ajustes.usuarios')}</h2>
        {usuarios.map((u) => (
          <div key={u.id} className="flex items-center justify-between">
            <span className={u.activo ? '' : 'text-slate-500 line-through'}>{u.nombre}</span>
            <span className="text-sm text-slate-400">{etiquetaRol(u.rol as Role)}</span>
          </div>
        ))}
        {/* Everybody's own PIN is theirs to change; the owner changes everybody else. */}
        {sesion && (
          <button
            type="button"
            className="btn-secundario mt-1 h-12 min-h-0 gap-2 text-sm"
            onClick={() => setCambiandoPin(true)}
          >
            <Ico icono="entrar" className="size-4" />
            {t('personal.miPin')}
          </button>
        )}
        {puedeGestionarUsuarios && (
          <Link to="/ajustes/personal" className="btn-secundario h-12 min-h-0 gap-2 text-sm">
            <Ico icono="personal" className="size-4" />
            {t('personal.administrar')}
          </Link>
        )}
      </section>

      {/* Only for somebody who can open it: a door that only says "this is the owner's" is worse than none. */}
      {puedeVerBitacora && (
        <Link
          to="/bitacora"
          className={`tarjeta-accion flex items-center gap-3 ${
            rechazados.length > 0 ? 'ring-rose-500/30' : ''
          }`}
        >
          <span className="flex-1 text-sm font-medium">{t('ajustes.bitacora')}</span>
          <span className={`text-xs ${rechazados.length > 0 ? 'text-rose-300' : 'text-slate-500'}`}>
            {rechazados.length > 0
              ? t('ajustes.rechazados', { n: rechazados.length })
              : t('ajustes.bitacoraTodo')}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-slate-600" />
        </Link>
      )}

      <DatosDePrueba />

      {cambiandoPin && <CambiarMiPin onCerrar={() => setCambiandoPin(false)} />}

      {renombrando && (
        <PedirDato
          titulo={t('arranque.nombre.titulo')}
          etiqueta={t('comun.nombre')}
          maxLength={60}
          ayuda={t('arranque.nombre.porque')}
          placeholder={t('arranque.nombre.ejemplo')}
          confirmar={t('comun.guardar')}
          icono="guardar"
          onCerrar={() => setRenombrando(false)}
          onListo={(valor) =>
            void emitir('config.cambiada', {
              clave: 'negocioNombre',
              valor,
              anterior: config?.negocioNombre ?? '',
            })
          }
        />
      )}

      {sesion && (
        <button type="button" className="btn-secundario w-full" onClick={() => void salir()}>
          <Ico icono="salir" />
          {t('ajustes.salir')}
        </button>
      )}
    </div>
  );
}

/**
 * The receipt printer's roll, on this device: the counter's printer and the driver's are
 * different printers. Most take 58mm; an 80mm one prints the same receipt with wider margins.
 */
function Impresora({ negocio }: { negocio: string }) {
  const t = useT();
  const [papel, setPapel] = useState<Papel>(papelActual);

  return (
    <section className="tarjeta flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <span className="etiqueta">{t('ajustes.impresora')}</span>
        <div className="flex gap-1 rounded-xl bg-slate-800 p-1">
          {(['58', '80'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                elegirPapel(p);
                setPapel(p);
              }}
              data-sin-icono="pestana"
              className={`flex min-h-9 flex-1 items-center justify-center rounded-lg px-3 text-sm transition ${
                p === papel ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              {t('ajustes.papel', { mm: p })}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500">{t('ajustes.ayudaImpresora')}</p>
      </div>
      <button type="button" className="btn-secundario" onClick={() => imprimirTexto(textoPrueba(negocio), papel)}>
        <Ico icono="imprimir" />
        {t('ajustes.imprimirPrueba')}
      </button>
    </section>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-sm text-slate-400">{etiqueta}</span>
      <span className="tabular text-sm font-medium">{valor}</span>
    </div>
  );
}
